import { useState } from 'react';
import TournamentHero from './components/TournamentHero';
import ActiveTournaments from './components/ActiveTournaments';
import UpcomingEvents from './components/UpcomingEvents';
import TournamentResults from './components/TournamentResults';
import { supabase } from '../../supabaseClient';

export default function TournamentsPage() {
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleRegister = async (tournamentName: string) => {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setMessage({ type: 'error', text: 'Please sign in to join tournaments' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSelectedTournament(tournamentName);
    setShowJoinModal(true);
    setMessage(null);
  };

  const handleJoinTournament = async (tournamentId: string, teamName?: string) => {
    setIsSubmitting(true);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setMessage({ type: 'error', text: 'Please sign in to join tournaments' });
        setIsSubmitting(false);
        return;
      }

      // Check if already registered
      const { data: existing } = await supabase
        .from('tournament_participants')
        .select('id')
        .eq('tournament_id', tournamentId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        setMessage({ type: 'error', text: 'You are already registered for this tournament!' });
        setIsSubmitting(false);
        return;
      }

      // Register for tournament
      const { error: insertError } = await supabase
        .from('tournament_participants')
        .insert({
          tournament_id: tournamentId,
          user_id: user.id,
          team_name: teamName || null,
          registered_at: new Date().toISOString()
        });

      if (insertError) throw insertError;

      // Update participant count
      const { data: tournament } = await supabase
        .from('tournaments')
        .select('current_participants')
        .eq('id', tournamentId)
        .maybeSingle();

      if (tournament) {
        await supabase
          .from('tournaments')
          .update({ 
            current_participants: (tournament.current_participants || 0) + 1 
          })
          .eq('id', tournamentId);
      }

      setMessage({ type: 'success', text: 'Successfully joined the tournament! Good luck!' });
      setTimeout(() => {
        setShowJoinModal(false);
        setMessage(null);
        window.location.reload(); // Refresh to show updated participant count
      }, 2000);

    } catch (error) {
      console.error('Error joining tournament:', error);
      setMessage({ type: 'error', text: 'Failed to join tournament. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <TournamentHero />
      <ActiveTournaments onRegister={handleRegister} />
      <UpcomingEvents onRegister={handleRegister} />
      <TournamentResults />

      {/* Join Tournament Modal */}
      {showJoinModal && (
        <JoinTournamentModal
          tournamentName={selectedTournament}
          onClose={() => {
            setShowJoinModal(false);
            setMessage(null);
          }}
          onJoin={handleJoinTournament}
          isSubmitting={isSubmitting}
          message={message}
        />
      )}
    </div>
  );
}

interface JoinTournamentModalProps {
  tournamentName: string;
  onClose: () => void;
  onJoin: (tournamentId: string, teamName?: string) => void;
  isSubmitting: boolean;
  message: { type: 'success' | 'error', text: string } | null;
}

function JoinTournamentModal({ tournamentName, onClose, onJoin, isSubmitting, message }: JoinTournamentModalProps) {
  const [teamName, setTeamName] = useState('');
  const [tournamentData, setTournamentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useState(() => {
    const fetchTournament = async () => {
      const { data } = await supabase
        .from('tournaments')
        .select('*')
        .eq('name', tournamentName)
        .maybeSingle();
      
      setTournamentData(data);
      setLoading(false);
    };
    fetchTournament();
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tournamentData) {
      onJoin(tournamentData.id, teamName || undefined);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-md w-full border border-gray-700">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Join Tournament</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-red-500 text-3xl"></i>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {message && (
              <div className={`px-4 py-3 rounded-lg border ${
                message.type === 'success' 
                  ? 'bg-green-500/10 border-green-500 text-green-400' 
                  : 'bg-red-500/10 border-red-500 text-red-400'
              }`}>
                <i className={`${message.type === 'success' ? 'ri-check-circle-fill' : 'ri-error-warning-fill'} mr-2`}></i>
                {message.text}
              </div>
            )}

            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-2">{tournamentName}</h3>
              {tournamentData && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Prize Pool:</span>
                    <span className="text-green-400 font-bold">${tournamentData.prize_pool?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Format:</span>
                    <span className="text-white capitalize">{tournamentData.format?.replace('-', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Starts:</span>
                    <span className="text-white">{new Date(tournamentData.start_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Spots Left:</span>
                    <span className="text-yellow-400">{tournamentData.max_participants - (tournamentData.current_participants || 0)}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="teamName" className="block text-sm font-medium text-gray-300 mb-2">
                Team Name (Optional)
              </label>
              <input
                type="text"
                id="teamName"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-red-500 transition-colors"
                placeholder="Enter your team name"
                maxLength={50}
              />
              <p className="text-gray-500 text-xs mt-1">Leave blank if competing solo</p>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="flex gap-3">
                <i className="ri-information-line text-yellow-400 text-xl flex-shrink-0"></i>
                <div className="text-sm text-yellow-200">
                  <p className="font-semibold mb-1">Before you join:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Make sure you're available on the tournament date</li>
                    <li>• Review the tournament rules and format</li>
                    <li>• Check your profile settings are up to date</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || message?.type === 'success'}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Joining...
                  </>
                ) : (
                  <>
                    <i className="ri-trophy-line mr-2"></i>
                    Confirm & Join
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
